import { ORG_MODULES_CHANGED_EVENT, parseOrgModules } from '../utils/orgModules';
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { TenantContext, Organization, TenantProviderProps } from '../types';
import { useAuth } from './AuthContext';
import { apiClient } from '../services/apiClient';
import { OrganizationsService, models_OrganizationResponse } from '../api/client';

const TenantContextInstance = createContext<TenantContext | null>(null);

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  // Theo dõi URL: platform admin đi /admin → /organizations/:slug trong SPA phải đổi org đang xem (ticket 113).
  const location = useLocation();
  const [slugState, setCurrentOrganization_slug] = useState<string | null>(null);
  // Platform admin: org đang xem suy THẲNG từ URL ở mỗi lần render (không qua effect → không bị trễ 1 nhịp/kẹt 'platform' khi
  // điều hướng /admin → /organizations/:slug, ticket 113). Org user giữ logic cũ (JWT/URL qua state).
  const urlOrgSlug = useMemo(() => {
    const seg = location.pathname.split('/');
    const i = seg.indexOf('organizations');
    return i >= 0 && seg[i + 1] && seg[0] === '' && i === 1 ? seg[i + 1] : null;
  }, [location.pathname]);
  const currentOrganization_slug = user?.user_type === 'platform_user' && isAuthenticated ? urlOrgSlug || 'platform' : slugState;
  const [isInitializing, setIsInitializing] = useState(true);
  // Module bật/tắt (ticket 120/122): lấy từ endpoint riêng /modules; vắng/lỗi = bật hết (không ẩn menu nào).
  const [orgModulesRaw, setOrgModulesRaw] = useState<Record<string, { enabled?: boolean }> | undefined>(undefined);
  const [fetchedOrgSettings, setFetchedOrgSettings] = useState<models_OrganizationResponse | null>(null);
  const [isLoadingOrgSettings, setIsLoadingOrgSettings] = useState(false);

  // Enhanced organization detection from URL and JWT data
  useEffect(() => {
    // Don't initialize until auth is done loading
    if (authLoading) {
      return;
    }

    // Reset if not authenticated
    if (!isAuthenticated) {
      setCurrentOrganization_slug(null);
      setIsInitializing(false);
      return;
    }

    // Extract organization slug from current URL
    const pathSegments = location.pathname.split('/');
    const orgFromUrl = pathSegments.find((_segment, index) =>
      pathSegments[index - 1] === 'organizations'
    );

    // For org users
    if (user?.user_type === 'org_user') {
      // Priority 1: Use organization from URL if available (handles page refresh)
      if (orgFromUrl) {
        setCurrentOrganization_slug(orgFromUrl);
      }
      // Priority 2: Fall back to JWT organization_slug
      else if (user.organization_slug) {
        setCurrentOrganization_slug(user.organization_slug);
      }
      // Priority 3: Extract from user's typical organization structure
      else {
        // This handles edge cases where JWT doesn't have organization_slug
        setCurrentOrganization_slug('demo-org'); // fallback for development
      }
      setIsInitializing(false);
      return;
    }

    // For platform users
    if (user?.user_type === 'platform_user') {
      if (orgFromUrl) {
        setCurrentOrganization_slug(orgFromUrl);
      } else {
        setCurrentOrganization_slug('platform');
      }
      setIsInitializing(false);
      return;
    }

    // Unknown user type - should not happen
    setCurrentOrganization_slug(null);
    setIsInitializing(false);
  }, [isAuthenticated, user, authLoading, location.pathname]);

  // Fetch organization settings from API when slug changes
  useEffect(() => {
    if (!currentOrganization_slug || currentOrganization_slug === 'platform' || !isAuthenticated) {
      setFetchedOrgSettings(null);
      return;
    }

    const fetchOrgSettings = async () => {
      setIsLoadingOrgSettings(true);
      try {
        const orgData = await OrganizationsService.getOrganizations1(currentOrganization_slug);
        setFetchedOrgSettings(orgData);
      } catch (error) {
        console.warn('Failed to fetch organization settings:', error);
        setFetchedOrgSettings(null);
      } finally {
        setIsLoadingOrgSettings(false);
      }
    };

    fetchOrgSettings();
  }, [currentOrganization_slug, isAuthenticated]);

  useEffect(() => {
    if (!currentOrganization_slug || currentOrganization_slug === 'platform' || !isAuthenticated) {
      setOrgModulesRaw(undefined);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const r = await apiClient.get<{ modules?: Record<string, { enabled?: boolean }>; configured?: boolean }>(
          `/settings/organizations/${currentOrganization_slug}/modules`,
        );
        if (!cancelled) setOrgModulesRaw(r?.configured ? r.modules : undefined);
      } catch {
        if (!cancelled) setOrgModulesRaw(undefined);
      }
    };
    load();
    window.addEventListener(ORG_MODULES_CHANGED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(ORG_MODULES_CHANGED_EVENT, load);
    };
  }, [currentOrganization_slug, isAuthenticated]);

  // Create organization data from JWT + API settings
  const organization = useMemo<Organization | null>(() => {
    if (!currentOrganization_slug || !isAuthenticated) return null;

    // For org users, create organization object merging JWT data with API settings
    // Platform admin xem một org (/organizations/:slug/*) cũng dựng đủ ngữ cảnh org như org_user (ticket 113).
    const platformInOrg = user?.user_type === 'platform_user' && currentOrganization_slug !== 'platform';
    if ((user?.user_type === 'org_user' || platformInOrg) && currentOrganization_slug) {
      // Default settings (fallback)
      const defaultSettings = {
        timezone: 'UTC',
        currency: 'USD',
        date_format: 'YYYY-MM-DD',
        payment_methods: ['card'],
        tax_settings: {
          enabled: false,
          default_rate: 0,
          tax_included_in_prices: false,
          tax_calculation_method: 'item_level',
        },
        notification_preferences: {
          email_notifications: true,
          sms_notifications: false,
          webhook_notifications: false,
        },
      };

      // Merge API settings with defaults (safely extract primitive values)
      const apiSettings = fetchedOrgSettings?.settings;
      const mergedSettings = {
        ...defaultSettings,
        // Only override with API values if they are valid primitives/arrays
        timezone: (typeof apiSettings?.timezone === 'string') ? apiSettings.timezone : defaultSettings.timezone,
        currency: (typeof apiSettings?.currency === 'string') ? apiSettings.currency : defaultSettings.currency,
        date_format: (typeof apiSettings?.date_format === 'string') ? apiSettings.date_format : defaultSettings.date_format,
        payment_methods: Array.isArray(apiSettings?.payment_methods) ? apiSettings.payment_methods : defaultSettings.payment_methods,
        tax_settings: {
          ...defaultSettings.tax_settings,
          ...(typeof apiSettings?.tax_settings === 'object' && apiSettings?.tax_settings ? apiSettings.tax_settings : {}),
        },
        notification_preferences: {
          ...defaultSettings.notification_preferences,
          ...(typeof apiSettings?.notification_preferences === 'object' && apiSettings?.notification_preferences ? apiSettings.notification_preferences : {}),
        },
        modules: orgModulesRaw ?? parseOrgModules((apiSettings as any)?.modules),
      };

      return {
        id: fetchedOrgSettings?.id || currentOrganization_slug,
        slug: fetchedOrgSettings?.slug || currentOrganization_slug,
        name: fetchedOrgSettings?.name || currentOrganization_slug.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()),
        email: `info@${currentOrganization_slug}.com`,
        enabled: fetchedOrgSettings?.active ?? true,
        created_at: fetchedOrgSettings?.created || new Date().toISOString(),
        subscription_tier: 'professional',
        features: {
          events_enabled: true,
          orders_enabled: true,
          payments_enabled: true,
          gift_cards_enabled: true,
          vouchers_enabled: true,
          dynamic_pricing_enabled: true,
          analytics_dashboard_enabled: true,
          webhooks_enabled: false,
          api_access_enabled: false,
          white_label_enabled: false,
          custom_domain_enabled: false,
          advanced_analytics_enabled: false,
          priority_support_enabled: false,
        },
        // BUG THẬT (logo tổ chức không hiện ở side menu) — bản trước hardcode branding, KHÔNG BAO GIỜ
        // đọc logo_url/primary_color/secondary_color THẬT đã lưu qua GeneralSettings.tsx (SettingsService
        // .putSettingsOrganizations, lưu vào CHÍNH `apiSettings`/settings.logo_url ở trên) — admin upload
        // logo xong, sidebar (TenantSider.tsx đọc `tenant.branding.logo_url`) vẫn không bao giờ thấy được
        // gì, vì trường nó đọc chưa từng được gán từ dữ liệu thật. Sửa: đọc từ `apiSettings`, cùng cách
        // phòng thủ (typeof === 'string') đã dùng cho timezone/currency ở trên, giữ nguyên màu mặc định
        // khi tổ chức chưa từng cấu hình.
        branding: {
          logo_url: (typeof apiSettings?.logo_url === 'string' && apiSettings.logo_url) ? apiSettings.logo_url : undefined,
          primary_color: (typeof apiSettings?.primary_color === 'string' && apiSettings.primary_color) ? apiSettings.primary_color : '#1890ff',
          secondary_color: (typeof apiSettings?.secondary_color === 'string' && apiSettings.secondary_color) ? apiSettings.secondary_color : '#52c41a',
        },
        settings: mergedSettings,
      };
    }

    return null;
  }, [currentOrganization_slug, user, isAuthenticated, fetchedOrgSettings, orgModulesRaw]);

  const organizationLoading = isInitializing || authLoading || isLoadingOrgSettings;

  // Get user permissions from JWT (no API call needed)
  const permissions = useMemo<string[]>(() => {
    if (!isAuthenticated || !user) return [];


    // Use permissions from JWT token
    const jwtPermissions = user.permissions || [];

    // For org users, generate typical organization permissions based on roles
    if (user.user_type === 'org_user') {
      const basePermissions = [...jwtPermissions];
      
      // Check both old and new role fields for compatibility
      const userRoles = user.roles || user.organization_roles || [];
      const hasOrgAdmin = userRoles.includes('org_admin');
      
      // Add organization-specific permissions based on roles
      if (hasOrgAdmin) {
        basePermissions.push(
          'organization:access',
          'organization:users:read',
          'organization:users:create', 
          'organization:users:update',
          'organization:settings:read',
          'organization:settings:update',
          'users:read',        // ✅ Add this for /organizations/:orgSlug/users endpoint
          'users:create',      // ✅ Add this for creating users
          'events:read',
          'events:create',
          'events:update',
          'events:delete',
          'orders:read',
          'orders:update',
          'analytics:read',
          'gift_cards:read',
          'vouchers:read'
        );
      }
      
      return basePermissions;
    }

    // For platform users
    if (user.user_type === 'platform_user') {
      return ['admin:all', 'platform:access', ...jwtPermissions];
    }

    return jwtPermissions;
  }, [user, isAuthenticated]);

  // Create tenant context value
  const tenantContext = useMemo<TenantContext | null>(() => {
    // For platform users accessing /admin, create synthetic context
    if (user?.user_type === 'platform_user' && location.pathname.startsWith('/admin')) {
      const platformContext: TenantContext = {
        organization_slug: 'platform',
        organization: {
          id: 'platform',
          slug: 'platform',
          name: 'Mantik Platform',
          email: 'admin@riptik.com',
          enabled: true,
          created_at: new Date().toISOString(),
          subscription_tier: 'enterprise',
          features: {
            events_enabled: true,
            orders_enabled: true,
            payments_enabled: true,
            gift_cards_enabled: true,
            vouchers_enabled: true,
            dynamic_pricing_enabled: true,
            analytics_dashboard_enabled: true,
            webhooks_enabled: true,
            api_access_enabled: true,
            white_label_enabled: true,
            custom_domain_enabled: true,
            advanced_analytics_enabled: true,
            priority_support_enabled: true,
          },
          branding: {
            primary_color: '#001529',
            secondary_color: '#1890ff',
          },
          settings: {
            timezone: 'UTC',
            currency: 'USD',
            date_format: 'YYYY-MM-DD',
            payment_methods: ['card', 'bank_transfer'],
            tax_settings: {
              enabled: false,
              default_rate: 0,
              tax_included_in_prices: false,
              tax_calculation_method: 'item_level',
            },
            notification_preferences: {
              email_notifications: true,
              sms_notifications: false,
              webhook_notifications: true,
            },
          },
        },
        features: {
          events_enabled: true,
          orders_enabled: true,
          payments_enabled: true,
          gift_cards_enabled: true,
          vouchers_enabled: true,
          dynamic_pricing_enabled: true,
          analytics_dashboard_enabled: true,
          webhooks_enabled: true,
          api_access_enabled: true,
          white_label_enabled: true,
          custom_domain_enabled: true,
          advanced_analytics_enabled: true,
          priority_support_enabled: true,
        },
        branding: {
          primary_color: '#001529',
          secondary_color: '#1890ff',
        },
        permissions: permissions || ['admin:all'],
        supported_languages: [
          { code: 'en', name: 'English' },
          { code: 'vi', name: 'Vietnamese' },
        ],
      };

      // Update API client with platform context
      apiClient.setTenantContext(platformContext);
      return platformContext;
    }

    // For organization users (and platform admin viewing an org), create context from JWT-based data
    if (user?.user_type === 'org_user' || (user?.user_type === 'platform_user' && currentOrganization_slug !== 'platform')) {
      if (!organization || !currentOrganization_slug) return null;

      const context: TenantContext = {
        organization_slug: currentOrganization_slug,
        organization,
        features: organization.features,
        branding: organization.branding,
        permissions: permissions,
        supported_languages: [
          { code: 'en', name: 'English' },
          { code: 'vi', name: 'Vietnamese' },
        ],
      };

      // Update API client with tenant context
      apiClient.setTenantContext(context);

      return context;
    }

    return null;
  }, [organization, permissions, currentOrganization_slug, user, location.pathname]);

  // No organization switching needed - users belong to single organization from JWT
  // Legacy methods kept for compatibility but not used
  const switchOrganization = () => {
    console.warn('switchOrganization called but not needed - users belong to single organization');
  };

  const clearOrganization = () => {
    console.warn('clearOrganization called but not needed - users belong to single organization');
  };

  // Enhanced context value with utility methods
  const contextValue = useMemo(() => {
    if (!tenantContext) return null;

    return {
      ...tenantContext,
      switchOrganization,
      clearOrganization,
      isLoading: organizationLoading,
    };
  }, [tenantContext, organizationLoading]);

  // Show loading while tenant context is initializing
  if (organizationLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div>Loading organization context...</div>
      </div>
    );
  }

  return (
    <TenantContextInstance.Provider value={contextValue}>
      {children}
    </TenantContextInstance.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContextInstance);
  return context; // Can be null for platform-wide pages
};

export const useRequiredTenant = (): TenantContext => {
  const context = useContext(TenantContextInstance);
  if (!context) {
    throw new Error('useRequiredTenant must be used within a tenant-specific route');
  }
  return context;
};